import React, { useState } from 'react';
import {
  History,
  FolderOpen,
  Download,
  Calendar,
  Layers,
  ArrowRight,
  RotateCcw,
  CheckCircle2,
  Clock,
  User as UserIcon,
} from 'lucide-react';
import { useReconciliation } from '../../context/ReconciliationContext';
import { HistoricalRun } from '../../types/auth';

interface HistoricalRunsProps {
  onSwitchToTable: () => void;
}

export const HistoricalRuns: React.FC<HistoricalRunsProps> = ({ onSwitchToTable }) => {
  const { historicalRuns, reopenRun } = useReconciliation();
  const [selectedForCompare, setSelectedForCompare] = useState<string[]>([]);

  const toggleCompare = (id: string) => {
    if (selectedForCompare.includes(id)) {
      setSelectedForCompare(selectedForCompare.filter((item) => item !== id));
    } else if (selectedForCompare.length < 2) {
      setSelectedForCompare([...selectedForCompare, id]);
    } else {
      setSelectedForCompare([selectedForCompare[1], id]);
    }
  };

  const handleReopen = (run: HistoricalRun) => {
    reopenRun(run);
    onSwitchToTable();
  };

  return (
    <div className="space-y-6">
      {/* Top Header */}
      <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-base font-bold text-slate-900 dark:text-white">
              Historical Reconciliation Archives
            </h2>
            <span className="text-[11px] px-2 py-0.5 rounded bg-brand-50 text-brand-700 dark:bg-brand-950 dark:text-brand-300 font-semibold border border-brand-200 dark:border-brand-800">
              {historicalRuns.length} Runs Logged
            </span>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            Reopen previous runs, inspect historical match ratios, compare batch metrics, and access audit snapshots.
          </p>
        </div>

        {selectedForCompare.length === 2 && (
          <div className="p-3 bg-brand-50 dark:bg-brand-950/60 rounded-xl border border-brand-200 dark:border-brand-800 text-xs flex items-center gap-3">
            <span className="font-semibold text-brand-900 dark:text-brand-200">
              Comparing 2 Batch Runs:
            </span>
            <span className="font-mono text-brand-700 dark:text-brand-300">
              {selectedForCompare.join(' vs ')}
            </span>
            <button
              onClick={() => setSelectedForCompare([])}
              className="text-xs underline text-slate-500 hover:text-slate-800"
            >
              Clear
            </button>
          </div>
        )}
      </div>

      {/* Historical Runs List */}
      {historicalRuns.length === 0 ? (
        <div className="bg-white dark:bg-slate-900 p-8 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm text-center">
          <History className="w-12 h-12 text-slate-400 mx-auto mb-3" />
          <h3 className="font-bold text-slate-800 dark:text-slate-200 text-sm">
            No Historical Runs Recorded Yet
          </h3>
          <p className="text-xs text-slate-500 mt-1">
            Execute a reconciliation run from the Upload section to archive sessions in Cloudflare D1.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {historicalRuns.map((run) => {
            const isComparing = selectedForCompare.includes(run.id);

            return (
              <div
                key={run.id}
                className={`p-5 rounded-2xl bg-white dark:bg-slate-900 border transition hover:shadow-md ${
                  isComparing
                    ? 'border-brand-500 ring-2 ring-brand-500/20'
                    : 'border-slate-200 dark:border-slate-800'
                }`}
              >
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  {/* Left Metadata */}
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2.5">
                      <span className="font-mono text-xs font-bold px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-200">
                        {run.id}
                      </span>
                      <h4 className="font-bold text-sm text-slate-900 dark:text-white">
                        {run.runName}
                      </h4>
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/60 px-2 py-0.5 rounded-full border border-emerald-200 dark:border-emerald-800">
                        <CheckCircle2 className="w-3 h-3" />
                        {run.summary.matchPercentage}% Match Rate
                      </span>
                    </div>

                    <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500 dark:text-slate-400">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" />
                        {new Date(run.createdAt).toLocaleString()}
                      </span>
                      <span>&bull;</span>
                      <span className="flex items-center gap-1">
                        <UserIcon className="w-3.5 h-3.5" />
                        {run.userEmail}
                      </span>
                      <span>&bull;</span>
                      <span>Razorpay: {run.razorpayFilename}</span>
                      <span>&bull;</span>
                      <span>ERP: {run.erpFilename}</span>
                    </div>
                  </div>

                  {/* Right KPI Highlights & Actions */}
                  <div className="flex items-center gap-4 justify-between lg:justify-end">
                    <div className="text-right font-mono text-xs hidden sm:block">
                      <div className="font-bold text-slate-900 dark:text-white">
                        ₹{run.summary.totalCollectionAmount.toLocaleString('en-IN')}
                      </div>
                      <div className="text-[11px] text-slate-400">
                        {run.summary.totalGroups} groups ({run.summary.exactMatches} exact)
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => toggleCompare(run.id)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition ${
                          isComparing
                            ? 'bg-brand-600 text-white border-brand-600'
                            : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-300 dark:border-slate-700 hover:bg-slate-50'
                        }`}
                      >
                        {isComparing ? 'Comparing' : 'Compare'}
                      </button>

                      <button
                        onClick={() => handleReopen(run)}
                        className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-xs font-semibold shadow-sm transition"
                      >
                        <FolderOpen className="w-3.5 h-3.5" />
                        <span>Reopen Session</span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Sub-metrics breakdown */}
                <div className="mt-4 pt-3 border-t border-slate-100 dark:border-slate-800 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2 text-xs text-slate-600 dark:text-slate-400">
                  <div>
                    <span className="text-[10px] text-slate-400 block">Exact Matches</span>
                    <span className="font-semibold text-emerald-600 dark:text-emerald-400 font-mono">
                      {run.summary.exactMatches}
                    </span>
                  </div>
                  <div>
                    <span className="text-[10px] text-slate-400 block">Partial Matches</span>
                    <span className="font-semibold text-amber-600 dark:text-amber-400 font-mono">
                      {run.summary.partialMatches}
                    </span>
                  </div>
                  <div>
                    <span className="text-[10px] text-slate-400 block">Over Billed</span>
                    <span className="font-semibold text-purple-600 dark:text-purple-400 font-mono">
                      {run.summary.overBilled}
                    </span>
                  </div>
                  <div>
                    <span className="text-[10px] text-slate-400 block">Missing ERP Bills</span>
                    <span className="font-semibold text-rose-600 dark:text-rose-400 font-mono">
                      {run.summary.noBillReference}
                    </span>
                  </div>
                  <div>
                    <span className="text-[10px] text-slate-400 block">Refunds</span>
                    <span className="font-semibold text-sky-600 dark:text-sky-400 font-mono">
                      {run.summary.refundedCount}
                    </span>
                  </div>
                  <div>
                    <span className="text-[10px] text-slate-400 block">Exceptions Total</span>
                    <span className="font-semibold text-slate-900 dark:text-white font-mono">
                      ₹{run.summary.totalExceptionAmount.toLocaleString('en-IN')}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
